import axios from "axios";
import environments from "../Utils/Environment";
import { setHeaderMessage } from "../Services/DashboardHeader/uiSlice";
import { toast } from "react-toastify";
import { tokenStorage, clearAuth } from "../Utils/localStorage";

const apiUrlStaging = environments.prod.url;

const axiosInstance = axios.create({
  baseURL: apiUrlStaging,
});

axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = tokenStorage.getAccessToken();

    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Flag and shared promise to avoid multiple parallel refresh calls
let isRefreshing = false;
let refreshPromise = null;

const logoutAndRedirect = async (message, redirectPath = "/") => {
  clearAuth();
  toast.error(message);
  const { default: store } = await import("../Redux/store");
  store.dispatch(setHeaderMessage("Community Management"));
  window.location.href = redirectPath;
};

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error || {};
    const originalRequest = config || {};

    // Never try to refresh a refresh-token request.
    if (originalRequest?.url?.includes("/auth/token/refresh/")) {
      await logoutAndRedirect("Session expired. You have been logged out.");
      return Promise.reject(error);
    }

    // Handle 401 with refresh token flow
    if (response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = tokenStorage.getRefreshToken();
      if (!refreshToken) {
        await logoutAndRedirect("Session expired. You have been logged out.");
        return Promise.reject(error);
      }

      try {
        if (!isRefreshing) {
          isRefreshing = true;
          refreshPromise = axios.post(`${apiUrlStaging}/auth/token/refresh/`, {
            refresh: refreshToken,
          });
        }

        const { data } = await refreshPromise;

        if (!data?.access) {
          throw new Error("Token refresh did not return a new access token.");
        }

        tokenStorage.setAccessToken(data.access);
        if (data?.refresh) {
          tokenStorage.setRefreshToken(data.refresh);
        }

        // Retry the original request with new access token
        originalRequest.headers = originalRequest.headers || {};
        originalRequest.headers.Authorization = `Bearer ${data.access}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        await logoutAndRedirect("Session expired. You have been logged out.");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
        refreshPromise = null;
      }
    }

    // Fallback 401 handling (e.g. invalid credentials, missing token, etc.)
    if (response?.status === 401 && response?.statusText === "Unauthorized") {
      await logoutAndRedirect("Session expired. You have been logged out.");
    } else if (response?.status === 403 && !originalRequest.skip403Redirect) {
      // Skip redirect if the request has skip403Redirect flag set to true
      await logoutAndRedirect(
        "Access forbidden. You have been logged out.",
        "/unauthorized"
      );
    } else if (response?.status >= 500 && !originalRequest.skip500Redirect) {
      // Handle server errors (500, 502, 503, etc.)
      // Only redirect if it's a critical error and not already handled
      const errorMessage =
        response?.data?.message ||
        "A server error occurred. Please try again later.";
      toast.error(errorMessage);

      // Optionally redirect to error page for critical server errors
      // Uncomment the line below if you want automatic redirect on 500 errors
      // window.location.href = "/error";
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
